import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Bulk-converts every PDF-attachment lesson in the chapter to the chosen
 * mode (slideshow by default). Sequential on the server so we don't hammer
 * the storage backend; UI polls a chapter-convert job for live progress.
 */
export default function ChapterConvertAllDialog({
    open,
    onOpenChange,
    chapterId,
    chapterTitle,
    onDone,
}) {
    const [job, setJob] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const pollRef = useRef(null);

    useEffect(() => {
        if (open) {
            setJob(null);
            setSubmitting(false);
        }
    }, [open]);

    useEffect(() => {
        if (!job?.id || job.status === "complete" || job.status === "failed") return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/chapter-convert-jobs/${job.id}`);
                setJob(data);
                if (data.status === "complete" || data.status === "failed") {
                    clearInterval(pollRef.current);
                    onDone?.();
                }
            } catch {
                /* ignore transient */
            }
        }, 2500);
        return () => clearInterval(pollRef.current);
    }, [job?.id, job?.status, onDone]);

    const start = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post(
                `/chapters/${chapterId}/lessons/convert-pdfs`,
                { mode: "slideshow", skip_already_converted: true },
            );
            if (!data.job_id) {
                toast.success(data.message || "Nothing to convert.");
                onOpenChange(false);
                return;
            }
            const initial = await api.get(`/chapter-convert-jobs/${data.job_id}`);
            setJob(initial.data);
            if (data.skipped?.length) {
                toast.message(
                    `${data.skipped.length} lesson${data.skipped.length === 1 ? "" : "s"} skipped (no PDF or already converted).`,
                );
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not start conversion.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const done = job?.status === "complete" || job?.status === "failed";
    const progress = job
        ? Math.round((job.current_index / Math.max(1, job.total)) * 100)
        : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl"
                data-testid="chapter-convert-all-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        Convert all to slideshow
                    </DialogTitle>
                    <DialogDescription>
                        {chapterTitle
                            ? `Render every PDF in "${chapterTitle}" as a paginated slideshow students can navigate, snip, and ask AI about.`
                            : "Render every PDF in this chapter as a slideshow."}
                    </DialogDescription>
                </DialogHeader>

                {!job ? (
                    <div className="space-y-3 py-2">
                        <p className="text-sm text-muted-foreground">
                            Lessons that have already been converted to slideshow are
                            skipped automatically. Each PDF is processed one at a time
                            (so big chapters take a few minutes — but the dialog stays
                            live and you can leave it open or come back later).
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3 py-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">
                                {done
                                    ? "Conversion complete"
                                    : `Converting ${Math.min(job.current_index + 1, job.total)} of ${job.total}…`}
                            </span>
                            <span className="text-muted-foreground tabular-nums">
                                {progress}%
                            </span>
                        </div>
                        <Progress
                            value={progress}
                            data-testid="chapter-convert-progress"
                        />
                        <ScrollArea
                            className="max-h-[280px] rounded-md border border-border overflow-hidden"
                            data-testid="chapter-convert-list"
                        >
                            {(job.lessons || []).map((ls, idx) => (
                                <div
                                    key={ls.id}
                                    className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border last:border-b-0 text-sm"
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <span className="text-[10px] text-muted-foreground tabular-nums w-5 text-right flex-shrink-0">
                                            {idx + 1}.
                                        </span>
                                        <span className="truncate" title={ls.title}>
                                            {ls.title}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        {ls.status === "done" ? (
                                            <span className="text-sky-600 inline-flex items-center gap-1 whitespace-nowrap">
                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                                <span className="text-xs">Done</span>
                                            </span>
                                        ) : ls.status === "failed" ? (
                                            <span
                                                className="text-destructive inline-flex items-center gap-1 max-w-[180px]"
                                                title={ls.error || "Failed"}
                                            >
                                                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                                                <span className="text-xs truncate">{ls.error || "Failed"}</span>
                                            </span>
                                        ) : ls.status === "running" ? (
                                            <span className="text-primary inline-flex items-center gap-1 whitespace-nowrap">
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                <span className="text-xs">Rendering…</span>
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground text-xs whitespace-nowrap">
                                                Queued
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </ScrollArea>
                    </div>
                )}

                <DialogFooter>
                    {!job ? (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                data-testid="chapter-convert-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={start}
                                disabled={submitting}
                                className="bg-primary hover:bg-primary/90 gap-1.5"
                                data-testid="chapter-convert-start"
                            >
                                {submitting ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Sparkles className="h-3.5 w-3.5" />
                                )}
                                <span>
                                    {submitting ? "Starting…" : "Start converting"}
                                </span>
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant={done ? "default" : "outline"}
                            onClick={() => onOpenChange(false)}
                            data-testid="chapter-convert-close"
                        >
                            {done ? "Close" : "Close (job keeps running)"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
